import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import lookie from 'lookie';
import { Input, Tooltip } from 'antd';
import { TrackerCustomEvent } from 'common-package';
import { Icon } from '@fuxi/eevee-icon';
import { selectedDeviceUuid, selectedStoreInfo, setStoreInfo } from '@/store/device';
import { Header, Toast } from '@/components';
import { useDevice, useNav, useTracker } from '@/hooks';
import { ALL_PAGES, CURRENT_ANDROID_ID } from '@/constant';
import service from '@/service';
import cx from './index.module.scss';

interface Props {}

const Settings: React.FC<Props> = () => {
  const { sendEvent } = useTracker();
  const deviceUuid = useSelector(selectedDeviceUuid);
  const storeInfo = useSelector(selectedStoreInfo);
  const nav = useNav();
  const { checkIsApp } = useDevice();
  const [editing, setEditing] = useState<boolean>(false);
  const [storeName, setStoreName] = useState<string>(null);
  const [toolTipOpen, setToolTipOpen] = useState<boolean>(false);
  const dispatch = useDispatch();

  const handleNetworkConfig = useCallback(() => {
    if (checkIsApp()) {
      nav(ALL_PAGES.NETWORK_CONFIG);
    } else {
      Toast.show('该功能仅可在PAD上使用');
    }
  }, [checkIsApp, nav]);

  const getBoundStore = useCallback(async () => {
    const androidId = lookie.get(CURRENT_ANDROID_ID);
    const { data } = await service.system.getBoundStore(androidId);
    dispatch(setStoreInfo(data));
    setEditing(false);
  }, [dispatch]);

  const handleUpdateStoreName = useCallback(async () => {
    try {
      await service.system.updateStoreName({ customName: storeName });
      await getBoundStore();
      Toast.success('修改成功');
    } catch (e) {
      Toast.fail(e?.message);
    } finally {
    }
  }, [getBoundStore, storeName]);

  return (
    <div className={cx('pad-info')}>
      <Header showBack={false} showBorder={false} />
      <div className={cx('content')}>
        <div className={cx('card-item', { 'is-editing-card': editing })}>
          <div className={cx('info-item', 'store-name')}>
            <div className={cx('label', { 'is-editing': editing })}>
              设备所属门店
              {storeInfo?.customName && (
                <Tooltip
                  title={`原始门店名称：${storeInfo?.name}`}
                  trigger={['click']}
                  open={toolTipOpen}
                  onOpenChange={open => setToolTipOpen(open)}
                >
                  <Icon name="info-circle" className={cx('tip-icon')} />
                </Tooltip>
              )}
            </div>
            <div className={cx('info-value')}>
              {editing ? (
                <>
                  <Input
                    className={cx('edit-name')}
                    value={storeName}
                    showCount
                    maxLength={20}
                    onChange={event => setStoreName(event.target?.value)}
                  />
                  <div className={cx('icon-wrapper', 'extra-wrapper')} onClick={handleUpdateStoreName}>
                    <Icon name="check-circle" className={cx('check-icon')} />
                  </div>
                  <div
                    className={cx('icon-wrapper')}
                    onClick={() => {
                      setEditing(false);
                      setStoreName(null);
                    }}
                  >
                    <Icon name="close-circle-2" className={cx('close-icon')} />
                  </div>
                </>
              ) : (
                <>
                  <span>{storeInfo.customName || storeInfo.name}</span>
                  <Icon
                    name="edit"
                    className={cx('icon')}
                    onClick={() => {
                      setEditing(true);
                      setStoreName(storeInfo.customName || storeInfo.name);
                    }}
                  />
                </>
              )}
            </div>
          </div>
          <div className={cx('line', { 'is-editing-line': editing })} />
          <div className={cx('info-item')}>
            <div className={cx('label')}>平板信息</div>
            <div className={cx('info-value')}>
              <div className={cx('device-uuid')}>{deviceUuid}</div>
            </div>
          </div>
          <div className={cx('info-item')}>
            <div className={cx('label')}>平板版本号</div>
            <div className={cx('info-value')}>v{__APP_VERSION__}</div>
          </div>
          <div className={cx('info-item')}>
            <div className={cx('label')}>版本发布时间</div>
            <div className={cx('info-value')}>{__UPDATE_TIME__}</div>
          </div>
        </div>
        <div className={cx('sub-module')}>
          <div className={cx('info-item', 'network-label')} onClick={handleNetworkConfig}>
            <div className={cx('label', 'network-label-text')}>设备配网</div>
            <div className={cx('network-icon-box')}>
              <Icon name="wifi" className={cx('network-icon')} />
            </div>
          </div>
          <div
            className={cx('info-item', 'network-label')}
            onClick={() => {
              sendEvent(TrackerCustomEvent.HelpFeedbackClk, { et: 'CLK' });
              nav(ALL_PAGES.HELP_FEEDBACK);
            }}
          >
            <div className={cx('label', 'network-label-text')}>帮助与反馈</div>
            <div className={cx('network-icon-box')}>
              <Icon name="help-feedback" size={26} className={cx('network-icon')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
